import React, { useState } from 'react';
import { Button, Col, Form, FormControl, FormGroup, Row } from 'react-bootstrap';
import { Condition, ConditionCriteria, ConditionProperties, ForwardRule } from 'reducers/nodesReducer';

type TConditionPopup = {
  isEditing: boolean; // if `isEditing` is true then `condition` is not null
  condition: Condition | null;
  rules: ForwardRule[];
  onClose: () => void;
  onSave: (_: Condition) => void;
};

function ConditionPopup({ isEditing, condition, rules, onClose, onSave }: TConditionPopup): React.ReactElement {
  const [source, setSource] = useState<number>(isEditing ? condition!.sourceNodeFk : -1);
  const [destination, setDestination] = useState<number>(isEditing ? condition!.destinationNodeFk : -1);
  const [property, setProperty] = useState<ConditionProperties | undefined>(
    isEditing ? condition!.property : undefined
  );
  const [criteria, setCriteria] = useState<ConditionCriteria | undefined>(
    isEditing ? condition!.criteria : undefined
  );
  const [referenceValue, setReferenceValue] = useState<string | undefined>(isEditing ? condition!.refValue : '');
  const [enabled, setEnabled] = useState<boolean>(isEditing ? condition!.enabled : false);

  const changeSourceDestination = srcDst => {
    const src = srcDst.split('_')[0];
    const dst = srcDst.split('_')[1];
    setSource(src);
    setDestination(dst);
  };

  const save = () => {
    if (source === -1 || destination === -1 || !property || !criteria || (!referenceValue && referenceValue !== '')) {
      return;
    }

    const temp = {
      pk: isEditing ? condition!.pk : -1,
      sourceNodeFk: source,
      destinationNodeFk: destination,
      property,
      criteria,
      refValue: referenceValue,
      enabled,
    };

    onSave(temp);
  };

  return (
    <Form>
      <FormGroup as={Row}>
        <Form.Label column sm={4} md={{ offset: 1 }} className="font-weight-bold">
          Forward rule
        </Form.Label>
        <Col sm={8}>
          <FormControl
            size="sm"
            as="select"
            defaultValue={isEditing ? [condition?.sourceNodeFk, condition!.sourceNodeFk].join('_') : -1}
            onChange={e => changeSourceDestination(e.target.value)}
            isInvalid={source === -1 && destination === -1}
            disabled={isEditing}
          >
            {isEditing ? (
              <option value={[condition?.sourceNodeFk, condition!.sourceNodeFk].join('_')} disabled>
                {condition!.sourceNodeAeTitle} → {condition!.destinationNodeAeTitle}
              </option>
            ) : (
              <option value={-1} disabled>
                Select nodes...
              </option>
            )}
            {isEditing
              ? undefined
              : rules.map((rule, index) => (
                  <option key={index} value={[rule.fromPk, rule.toPk].join('_')}>
                    {rule.from} → {rule.to}
                  </option>
                ))}
          </FormControl>
        </Col>
      </FormGroup>
      <FormGroup as={Row}>
        <Form.Label column sm={4} md={{ offset: 1 }} className="font-weight-bold">
          Properties
        </Form.Label>
        <Col sm={8}>
          <FormControl
            size="sm"
            as="select"
            defaultValue={isEditing ? property : undefined}
            onChange={e => setProperty(e.target.value as ConditionProperties)}
            isInvalid={!property}
          >
            {isEditing ? (
              <option value={property} disabled>
                {ConditionProperties[property!]}
              </option>
            ) : (
              <option value={undefined} disabled>
                Select property...
              </option>
            )}

            {Object.entries(ConditionProperties).map(([k, v]) => (
              <option key={k} value={k as ConditionProperties}>
                {v}
              </option>
            ))}
          </FormControl>
        </Col>
      </FormGroup>
      <FormGroup as={Row}>
        <Form.Label column sm={4} md={{ offset: 1 }} className="font-weight-bold">
          Criteria
        </Form.Label>
        <Col sm={8}>
          <FormControl
            size="sm"
            as="select"
            defaultValue={isEditing ? criteria : undefined}
            onChange={e => setCriteria(e.target.value as ConditionCriteria)}
            isInvalid={!criteria}
          >
            {isEditing ? (
              <option value={criteria} disabled>
                {ConditionCriteria[criteria!]}
              </option>
            ) : (
              <option value={undefined} disabled>
                Select criteria...
              </option>
            )}

            {Object.entries(ConditionCriteria).map(([k, v]) => (
              <option key={k} value={k as ConditionCriteria}>
                {v}
              </option>
            ))}
          </FormControl>
        </Col>
      </FormGroup>
      <FormGroup as={Row}>
        <Form.Label column sm={4} md={{ offset: 1 }} className="font-weight-bold">
          Reference value
        </Form.Label>
        <Col sm={8}>
          <FormControl
            size="sm"
            type="text"
            placeholder="Reference value..."
            value={referenceValue}
            onChange={e => setReferenceValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          />
        </Col>
      </FormGroup>
      <FormGroup as={Row}>
        <Form.Label column sm={4} md={{ offset: 1 }} className="font-weight-bold">
          Enable condition
        </Form.Label>
        <Col sm={8}>
          <Form.Check
            custom
            type="checkbox"
            id="enableCondition"
            label=""
            checked={enabled}
            onChange={() => setEnabled(!enabled)}
          />
        </Col>
      </FormGroup>
      <div style={{ textAlign: 'right' }}>
        <br />
        <Button onClick={onClose} variant="outline-secondary" style={{ marginRight: '5px' }}>
          Cancel
        </Button>
        <Button style={{ width: '8em' }} variant="primary" onClick={save}>
          Save
        </Button>
      </div>
    </Form>
  );
}

export default ConditionPopup;
